import {
  ENVIRONMENT_PRODUCTION,
  LS_FULL_STATE,
  LS_OAUTH_ERROR_KEY,
  LS_OAUTH_ONE_TIME_CODE,
  LS_OAUTH_REDIRECT_FINISHED,
} from "@superblocksteam/shared";
import type {
  AuthConfig,
  DatasourceAuthState,
  DatasourceOneTimeState,
} from "@superblocksteam/shared";

export type OAuthCallbackHandoff = "localStorage" | "postMessage";

type OAuthCallbackStorage = Pick<Storage, "getItem" | "removeItem" | "setItem">;

export function resolveOAuthCallbackHandoff(
  authState: DatasourceOneTimeState | DatasourceAuthState,
): OAuthCallbackHandoff {
  return authState.useLocalStorage === true ? "localStorage" : "postMessage";
}

/** First candidate that is a non-blank string, else `undefined`. */
function firstNonBlank(
  ...candidates: Array<string | null | undefined>
): string | undefined {
  return candidates.find(
    (candidate): candidate is string =>
      typeof candidate === "string" && candidate.trim() !== "",
  );
}

/**
 * Resolve the profile key (a.k.a. environment) the callback popup uses to
 * select an active agent, from whichever handoff carried it.
 *
 * Each handoff mode has its own source of truth:
 *   - `localStorage`: the opener-seeded `LS_OAUTH_ENV_KEY` value.
 *   - `postMessage` + embed (`externalUser`): the shell's postMessage reply,
 *     falling back to the OAuth state.
 *   - `postMessage` (non-embed): the OAuth state.
 *
 * A blank candidate (missing, `""`, or whitespace) is treated as absent so it
 * cannot leak through as the agent-selection environment — a blank environment
 * matches no agent's profile tag and hangs the callback on "Logging in"
 * (APPS-4543). When no candidate is meaningful we default to production, the
 * historical fallback for OAuth callbacks.
 */
export function resolveOAuthCallbackProfileKey({
  handoffMode,
  externalUser,
  localStorageProfileKey,
  shellProfileKey,
  stateProfileKey,
}: {
  handoffMode: OAuthCallbackHandoff;
  externalUser: boolean;
  localStorageProfileKey: string | null | undefined;
  shellProfileKey: string | null | undefined;
  stateProfileKey: string | null | undefined;
}): string {
  const resolved =
    handoffMode === "localStorage"
      ? firstNonBlank(localStorageProfileKey)
      : externalUser
        ? firstNonBlank(shellProfileKey, stateProfileKey)
        : firstNonBlank(stateProfileKey);
  return resolved ?? ENVIRONMENT_PRODUCTION;
}

export function completeLocalStorageOAuthCallback({
  error,
  storage,
}: {
  error?: string;
  storage: OAuthCallbackStorage;
}): void {
  if (error) {
    storage.setItem(LS_OAUTH_ERROR_KEY, error);
  } else {
    storage.removeItem(LS_OAUTH_ERROR_KEY);
  }
  storage.setItem(LS_OAUTH_REDIRECT_FINISHED, "true");
  storage.removeItem(LS_FULL_STATE);
}

export function validateLocalStorageOneTimeCodeIfApplicable(
  state: DatasourceOneTimeState,
  authConfig: AuthConfig,
  storage: OAuthCallbackStorage,
): boolean {
  if (!authConfig.sendOAuthState) {
    return true;
  }
  return state.oneTimeCode === storage.getItem(LS_OAUTH_ONE_TIME_CODE);
}

export function validateOAuthCallbackOneTimeCodeIfApplicable({
  authConfig,
  handoffMode,
  postMessageOneTimeCodeVerified = false,
  state,
  storage,
}: {
  authConfig: AuthConfig;
  handoffMode: OAuthCallbackHandoff;
  postMessageOneTimeCodeVerified?: boolean;
  state: DatasourceOneTimeState;
  storage: OAuthCallbackStorage;
}): boolean {
  if (!authConfig.sendOAuthState) {
    return true;
  }
  if (handoffMode === "postMessage" && postMessageOneTimeCodeVerified) {
    return true;
  }
  if (handoffMode === "postMessage") {
    // Deployed-shell / embed openers put the nonce in OAuth state and
    // correlate postMessage by oneTimeCode. The popup's localStorage
    // partition is not shared with the opener (3PCD / ITP), so the
    // legacy opener-seeded key is absent even for valid flows.
    return Boolean(state.oneTimeCode);
  }
  return validateLocalStorageOneTimeCodeIfApplicable(
    state,
    authConfig,
    storage,
  );
}
